/**
 * EditLockContext - Sistema de bloqueo durante edición activa
 *
 * @version 1.0
 * @updated 2026-02-25
 * @module core/layouts
 *
 * @description
 * Contexto global que gestiona el estado de "edición activa" en la plataforma.
 * Cuando hay una edición activa con cambios no guardados, bloquea:
 * - Cambio de modo operacional
 * - Cambio de módulo
 * - Herramientas de creación
 * - Selección de otros elementos
 * - Clicks en el mapa
 *
 * @remarks
 * El usuario DEBE guardar o cancelar antes de poder hacer otras acciones.
 * Solo las herramientas de análisis (calculator, notes, measurement) permanecen activas.
 */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

/**
 * Tipo de elemento que se está editando
 */
export type EditingElementType = 'tree' | 'container' | 'cell' | 'grid' | 'route' | null;

/**
 * Estado del contexto
 */
export interface EditLockState {
  /** Indica si hay una edición activa */
  isEditingActive: boolean;
  /** Tipo de elemento que se está editando */
  editingElementType: EditingElementType;
  /** Indica si hay cambios no guardados */
  hasUnsavedChanges: boolean;
}

/**
 * Acciones del contexto
 */
export interface EditLockActions {
  /**
   * Activa el bloqueo de edición
   * @param elementType - Tipo de elemento que se está editando
   */
  startEditing: (elementType: EditingElementType) => void;

  /**
   * Desactiva el bloqueo de edición
   */
  stopEditing: () => void;

  /**
   * Marca que hay cambios no guardados
   */
  markUnsavedChanges: () => void;

  /**
   * Marca que los cambios han sido guardados
   */
  markChangesSaved: () => void;
}

/**
 * Tipo completo del contexto
 */
export type EditLockContextType = EditLockState & EditLockActions;

/**
 * Contexto
 */
const EditLockContext = createContext<EditLockContextType | undefined>(undefined);

/**
 * Props del provider
 */
export interface EditLockProviderProps {
  children: ReactNode;
}

/**
 * Provider del contexto
 */
export function EditLockProvider({ children }: EditLockProviderProps): JSX.Element {
  const [isEditingActive, setIsEditingActive] = useState(false);
  const [editingElementType, setEditingElementType] = useState<EditingElementType>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /**
   * Activa el bloqueo de edición
   */
  const startEditing = useCallback((elementType: EditingElementType): void => {
    console.log('[EditLock] Iniciando edición:', elementType);
    setIsEditingActive(true);
    setEditingElementType(elementType);
    setHasUnsavedChanges(false); // Inicia sin cambios
  }, []);

  /**
   * Desactiva el bloqueo de edición
   */
  const stopEditing = useCallback((): void => {
    console.log('[EditLock] Finalizando edición');
    setIsEditingActive(false);
    setEditingElementType(null);
    setHasUnsavedChanges(false);
  }, []);

  /**
   * Marca que hay cambios no guardados
   */
  const markUnsavedChanges = useCallback((): void => {
    console.log('[EditLock] Cambios no guardados detectados');
    setHasUnsavedChanges(true);
  }, []);

  /**
   * Marca que los cambios han sido guardados
   */
  const markChangesSaved = useCallback((): void => {
    console.log('[EditLock] Cambios guardados');
    setHasUnsavedChanges(false);
  }, []);

  const value: EditLockContextType = {
    // Estado
    isEditingActive,
    editingElementType,
    hasUnsavedChanges,
    // Acciones
    startEditing,
    stopEditing,
    markUnsavedChanges,
    markChangesSaved,
  };

  return <EditLockContext.Provider value={value}>{children}</EditLockContext.Provider>;
}

/**
 * Hook para usar el contexto
 */
export function useEditLock(): EditLockContextType {
  const context = useContext(EditLockContext);
  if (!context) {
    throw new Error('useEditLock must be used within EditLockProvider');
  }
  return context;
}

/**
 * Hook seguro que retorna null si no hay EditLockProvider
 */
export function useEditLockSafe(): EditLockContextType | null {
  return useContext(EditLockContext) ?? null;
}
